// MACHINERY V2 (Stroje) — manufacturer facet grouping + the CLIENT-SIDE brand
// search (M2.8).
//
// Spec: data package §11 — the `manufacturerFacet.*` field row
// ("childManufacturers length>0 marks a parent group") and the `brandSearch`
// filter row ("Filters loaded facet list CLIENT-SIDE; never hits API. Distinct
// from query.search"). Reference impl: components/CatalogBrowsePage.tsx
// :296-362 (the grouping useMemo) — this is that block lifted out of React and
// made pure.
//
// >>> TWO SEARCHES, TWO SCOPES. DO NOT MERGE THEM. <<<
//   · `query.search` is the MODEL search: server-side, 2-char minimum, 300ms
//     debounce, re-fetches the page, counts toward activeFilterCount.
//   · `brandSearch` is a substring filter over the ALREADY-LOADED facet list.
//     It never touches the network and never counts as a filter — it only
//     decides which brand rows the rail draws. Sending it to the API would
//     silently change the RESULT set, which is not what the box promises.
//
// Artboard 0900/0901 draw the rail with "Zobraziť všetkých 214 →" under a
// truncated list; `visibleFacetRows` implements that cap, and a brand search
// bypasses it (a searched-for brand must be reachable without expanding first).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { CatalogManufacturer } from "../../models/models";

export interface CatalogManufacturerGroup {
  parent: CatalogManufacturer;
  children: CatalogManufacturer[];
  /** Parent's own count + every child's — the facet pill the artboard draws. */
  aggregateCount: number;
}

export interface CatalogFacetModel {
  groups: CatalogManufacturerGroup[];
  standalone: CatalogManufacturer[];
  /** Parents a brand search matched (directly or via a child) — the rail
   *  auto-expands these so the match is visible without a click. */
  matchedParentIds: Set<string>;
  /** Rows the rail would draw with no cap applied — drives "show all N". */
  totalRows: number;
}

/** How many top-level brand rows the rail shows before "Zobraziť všetkých N".
 *  Artboards 0900/0901 draw 8 (Mazak … Tajmac-ZPS) above the expander. */
export const CATALOG_BRAND_ROW_CAP = 8;

/**
 * v1's grouping, unchanged in behaviour:
 *   · a manufacturer with `childManufacturers.length > 0` is a PARENT;
 *   · every id appearing in some parent's `childManufacturers` is a CHILD and
 *     is never drawn at top level, even if the facet list also returns it flat;
 *   · everything else is STANDALONE.
 * Children are re-derived from `parentManufacturerId` rather than read off
 * `childManufacturers`, because only the flat rows carry `machineCount` —
 * `CatalogManufacturerSummaryViewModel` (the nested shape) lacks it (§11's
 * closing "Catalog-vs-custom" paragraph). Sorting is by count, descending,
 * both levels.
 */
export const buildCatalogFacets = (
  manufacturers: CatalogManufacturer[],
  brandSearch: string
): CatalogFacetModel => {
  const needle = brandSearch.trim().toLowerCase();

  const parentIds = new Set<string>();
  const childIds = new Set<string>();
  manufacturers.forEach((m) => {
    if (m.childManufacturers && m.childManufacturers.length > 0) {
      parentIds.add(m.id);
      m.childManufacturers.forEach((c) => childIds.add(c.id));
    }
  });

  const groups: CatalogManufacturerGroup[] = [];
  const standalone: CatalogManufacturer[] = [];
  const matchedParentIds = new Set<string>();

  manufacturers.forEach((m) => {
    if (childIds.has(m.id)) return;

    if (parentIds.has(m.id)) {
      const children = manufacturers
        .filter((c) => c.parentManufacturerId === m.id)
        .sort((a, b) => b.machineCount - a.machineCount);

      if (needle) {
        const parentHit = m.name.toLowerCase().indexOf(needle) >= 0;
        const childHit = children.some((c) => c.name.toLowerCase().indexOf(needle) >= 0);
        if (!parentHit && !childHit) return;
        matchedParentIds.add(m.id);
      }

      groups.push({
        parent: m,
        children,
        aggregateCount:
          m.machineCount + children.reduce((sum, c) => sum + c.machineCount, 0),
      });
      return;
    }

    if (needle && m.name.toLowerCase().indexOf(needle) < 0) return;
    standalone.push(m);
  });

  groups.sort((a, b) => b.aggregateCount - a.aggregateCount);
  standalone.sort((a, b) => b.machineCount - a.machineCount);

  return {
    groups,
    standalone,
    matchedParentIds,
    totalRows: groups.length + standalone.length,
  };
};

export interface CatalogFacetRow {
  id: string;
  name: string;
  count: number;
  /** Present only on a parent row — the rail draws its expander chevron. */
  children?: CatalogManufacturer[];
}

/**
 * TRUE while the row cap does not apply because a brand search is running.
 * Exported so the rail's expander and `visibleFacetRows` cannot disagree about
 * it: the expander used to keep its own `expanded` state while a search
 * silently bypassed the cap, which produced a "Show fewer" button that changed
 * nothing on screen and then relabelled itself "Show all N" — one list
 * reporting two states. Whoever draws the control asks the same question the
 * row builder asks.
 */
export const catalogFacetCapBypassed = (brandSearch: string): boolean =>
  brandSearch.trim().length > 0;

/**
 * Groups first, then standalones — the artboard order (parent families above
 * the long flat tail). Capped unless the caller expanded the list or a brand
 * search is active: a search that hid its own match behind "show all" would be
 * a dead end.
 */
export const visibleFacetRows = (
  facets: CatalogFacetModel,
  expanded: boolean,
  brandSearch: string
): CatalogFacetRow[] => {
  const rows: CatalogFacetRow[] = [
    ...facets.groups.map((g) => ({
      id: g.parent.id,
      name: g.parent.name,
      count: g.aggregateCount,
      children: g.children,
    })),
    ...facets.standalone.map((s) => ({
      id: s.id,
      name: s.name,
      count: s.machineCount,
    })),
  ];

  if (expanded || catalogFacetCapBypassed(brandSearch)) return rows;
  return rows.slice(0, CATALOG_BRAND_ROW_CAP);
};
